/* ============================================================
   Component · Button roll - the duplicates

   CARRIED VERBATIM from the marketing theme's
   `assets/src/js/components/button-roll.js` (pass 395.18), under the same
   replace-not-merge rule as the shared SCSS.

   Hub had `button.scss` byte-for-byte and this file not at all, which is the
   fork problem in its purest form: the CSS was complete, so nothing looked
   missing, and every button on the public frontend answered a pointer by rolling
   its label UP and leaving an empty window behind it. The copy that should have
   arrived is a `::after` reading `content: attr(data-roll)`, and nothing was
   filling that attribute.

   The roll itself is entirely CSS (components/_button.scss). The one thing CSS
   cannot do is know what a button says: the copy that slides up from below is a
   `::after` reading `content: attr(data-roll)`, and something has to put the
   label's own text into that attribute.

   NOT IN THE MARKUP, which is the whole reason this file exists. Writing the label
   twice would mean editing 25 call sites across 17 templates, and then remembering
   to do it in every button written after today - a decoration that can be
   forgotten is a decoration that WILL be, and the failure is silent (the button
   just stops rolling). It also would not cover the search dialog, whose buttons
   Alpine renders at runtime and which no template contains.

   LAZILY, ON FIRST HOVER, rather than in a pass at load. A pass would have to run
   again for every button that appears later - Alpine's, anything a future fetch
   drops in - which means either a MutationObserver watching the whole document
   forever, or a bug the first time somebody adds one. Filling the attribute at the
   moment a pointer or focus arrives needs neither: nothing can roll before it is
   hovered, so nothing can be missed.

   TWO LISTENERS ON THE DOCUMENT, both passive, both delegated. `pointerover`
   rather than `mouseenter` because the latter does not bubble, and `focusin`
   rather than `focus` for the same reason - a keyboard user gets the roll too.

   ── THE ATTRIBUTE IS A CACHE, SO IT NEEDS INVALIDATING
   A label that CHANGES while the pointer is on it is the case this got wrong.
   The promo card's copy button is the one that does: press it and Alpine rewrites
   the label to "Copied" - but the attribute had been filled on the way in and was
   never looked at again, so the visible text said "Copied" and the copy sliding up
   underneath it still said "Copy". Two answers to one press, on one control.

   Each label that gets primed is WATCHED from then on, and re-primed whenever its
   text changes. An observer per label rather than one over the document: the set
   is created lazily and is exactly the buttons somebody has actually touched, so
   nothing is watched speculatively. Re-priming on the next hover instead would
   have fixed nothing here - the pointer never left.
   ============================================================ */

/** Labels already under observation, so a second hover does not add a second one. */
const watched = new WeakSet();

/**
 * Copy a label's text into the attribute the CSS duplicate reads.
 *
 * Reads `textContent`, not `innerHTML`: the copy is a CSS `content` string, so any
 * markup inside the label (a marked word, a nested span) would arrive as literal
 * angle brackets. The visible label keeps its markup; the copy is its plain text,
 * which is all a half-second slide needs to be.
 */
function prime(label) {
    const mover = label.firstElementChild;

    // A label written as a single span has nothing to move - the CSS says so too,
    // and it degrades to a static label rather than breaking.
    if (!mover) {
        return;
    }

    const text = mover.textContent.trim();
    if (text === '') {
        return;
    }

    // Compared rather than written blind: this now runs on every hover AND on every
    // change, and an attribute write on an element mid-transition is a style
    // invalidation for a value that is usually identical.
    if (mover.dataset.roll !== text) {
        mover.dataset.roll = text;
    }
}

/**
 * Keep a primed label's duplicate in step with its own text.
 *
 * ONE OBSERVER PER LABEL, attached the first time it is primed and never removed -
 * it lives exactly as long as the button does. `characterData` with `subtree`
 * because the change can arrive either way: Alpine's `x-text` replaces the text
 * node, and anything writing `textContent` on a nested element only mutates it.
 */
function watch(label) {
    const mover = label.firstElementChild;
    if (!mover || watched.has(mover)) {
        return;
    }
    watched.add(mover);

    new MutationObserver(() => prime(label)).observe(mover, {
        childList: true,
        characterData: true,
        subtree: true,
    });
}

/**
 * Prime every label inside whatever the pointer or focus just reached.
 *
 * The target is not necessarily the button: a pointer crossing a button lands on
 * whatever is under it, which may be the icon, the label, or the label's own span.
 * `closest()` walks up to the control, and the query then walks back down - a
 * button can hold more than one label (none do today, and one day one will).
 */
function primeFrom(event) {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }

    const button = target.closest('.jkd-button');
    if (!button) {
        return;
    }

    button.querySelectorAll('.jkd-button__label').forEach((label) => {
        prime(label);
        watch(label);
    });

    // The link variant only - every other button rolls its label, and a control
    // making the gesture twice at once is two gestures.
    if (button.classList.contains('jkd-button--link')) {
        primeIcon(button);
    }
}

/* ── THE LINK VARIANT ROLLS ITS ICON ─────────────────────────────────────────
   `.jkd-button--link` is the one variant whose LABEL does not roll: its hover is
   the rule travelling under the words, and the roll would carry that rule off the
   top of the control with the text it is painted on (components/_button.scss says
   so at the exclusion). So the variant answered a pointer with a line and a colour
   while every pill beside it flipped, and the icon at the end of it - the arrow
   that is usually the whole point of the control - sat still.

   The glyph takes the gesture, exactly as it does on the icon-only button. Same
   window, same distance, same clock, same smear: this is the SAME hover, applied to
   the only part of the control that is free to move.

   BUILT HERE RATHER THAN IN THE MARKUP, for the reason the label's duplicate is:
   the roll needs a second glyph, and writing one into every link-style control on
   the site is a decoration that can be forgotten and silently will be. A clone is
   also the only correct copy - the glyph is a codepoint on a `::before` from an
   icon font, so nothing in CSS can read it and nothing in a template should have
   to know it.

   TWO SHAPES OF MARKUP, ONE RESULT. A link button's icon is either a
   `.jkd-button__icon` box or a bare `<i>` / `<svg>` sitting beside the label. The
   box becomes the window in place; the bare glyph gets a span wrapped round it.
   Either way the button ends up with a `.jkd-button__icon--roll` window holding two
   copies, which is the markup the icon-only button has always been written with by
   hand - so there is one roll in the stylesheet, not a second one for this case. */
const ICON = ':scope > .jkd-button__icon, :scope > i, :scope > svg';

function primeIcon(button) {
    // Already built, by this script or by a template that wrote it out.
    if (button.querySelector('.jkd-button__icon--roll')) {
        return;
    }

    const icon = button.querySelector(ICON);
    if (!icon) {
        return;
    }

    // The box shape: it becomes the window, and its glyph is the thing to copy.
    // Anything already holding more than one child was authored deliberately and is
    // left alone.
    if (icon.classList.contains('jkd-button__icon')) {
        if (icon.childElementCount !== 1) {
            return;
        }
        icon.classList.add('jkd-button__icon--roll');
        icon.append(copyOf(icon.firstElementChild));
        settle(button);
        return;
    }

    // The bare shape: the glyph cannot be its own window - it is the thing that
    // moves - so it gets one. The span takes the glyph's place in the button first
    // and the glyph goes back inside it, which keeps the icon at the same position
    // among the button's children and so keeps `:has(> …)` matching throughout.
    const window_ = document.createElement('span');
    window_.className = 'jkd-button__icon--roll';
    icon.replaceWith(window_);
    window_.append(icon, copyOf(icon));
    settle(button);
}

/**
 * Give the just-built glyphs a resting frame to leave from.
 *
 * THE ROLL IS BUILT INSIDE THE HOVER THAT PLAYS IT, which is the one thing lazy
 * construction costs and it cost the whole gesture. A transition travels between
 * two computed values; an element created a moment ago has only one, and by the
 * time it is computed the button is already `:hover`, so both copies were born at
 * the END of the roll and got there in a single frame. The smear on the window is a
 * keyframe animation and those DO run on a fresh element, so the first hover of
 * every link-style button on the site was a blur over an icon that never moved -
 * and every hover after it was correct, because by then the elements had a rest
 * style. That split is what made it read as a rendering glitch rather than as a
 * missing gesture.
 *
 * `.is-priming` switches the hover response off (components/_button.scss), reading
 * the width forces the style to be computed while it is off, and taking the class
 * away again is a change the transition can travel on. All three in one tick: the
 * pointer is already here, and the roll has to start now, not next frame.
 *
 * The forced read is the mechanism, not a leftover - and deliberately not a
 * `requestAnimationFrame`, which never fires in a background tab and would leave
 * the glyphs pinned in the rest frame there.
 */
function settle(button) {
    button.classList.add('is-priming');
    void button.offsetWidth;
    button.classList.remove('is-priming');
}

/**
 * The waiting copy of a glyph.
 *
 * `cloneNode(true)` keeps the classes an icon font draws through, which is the only
 * way to reproduce a codepoint nothing here can read. It is decoration by
 * construction - the control carries its name in its label or its `aria-label` -
 * and the original may not have said so, so this one always does.
 */
function copyOf(glyph) {
    const copy = glyph.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('id');
    return copy;
}

document.addEventListener('pointerover', primeFrom, { passive: true });
document.addEventListener('focusin', primeFrom, { passive: true });
